import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Mail, 
  ExternalLink,
  Search,
  ChevronRight,
  Eye,
  CheckCircle2,
  AlertCircle,
  Send,
  Clock,
  FileEdit,
  RefreshCw,
  XCircle,
  Ban,
  Building2,
  User,
  Star,
  DollarSign,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  SkipForward,
  UserX,
  Building,
  FileText,
  MapPin,
  Link
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colors
const STATUS_STYLES = {
  sent: { bg: "bg-green-100", text: "text-green-700", label: "Sent" },
  stored: { bg: "bg-blue-100", text: "text-blue-700", label: "Stored" },
  failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed" },
  blocked: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Blocked" },
  // Phase 3A statuses
  draft_ready: { bg: "bg-purple-100", text: "text-purple-700", label: "Draft Ready" },
  review_required: { bg: "bg-orange-100", text: "text-orange-700", label: "Review Required" },
  approved: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Approved" },
  rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejected" },
  skipped: { bg: "bg-gray-100", text: "text-gray-700", label: "Skipped" },
  replied: { bg: "bg-blue-100", text: "text-blue-700", label: "Replied" },
  bounced: { bg: "bg-red-100", text: "text-red-700", label: "Bounced" },
  suppressed: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Suppressed" }
};

// Tier badge colors
const TIER_STYLES = {
  S: { bg: "bg-purple-100", text: "text-purple-700" },
  A: { bg: "bg-blue-100", text: "text-blue-700" },
  B: { bg: "bg-green-100", text: "text-green-700" },
  C: { bg: "bg-yellow-100", text: "text-yellow-700" },
  D: { bg: "bg-gray-100", text: "text-gray-700" }
};

export default function EmailOutbox() {
  const [emails, setEmails] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedReviewItem, setSelectedReviewItem] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [safetyStatus, setSafetyStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("review");
  const [editingBody, setEditingBody] = useState("");
  const [editingSubject, setEditingSubject] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [renderedPreview, setRenderedPreview] = useState(null);
  const [showRenderedPreview, setShowRenderedPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchEmails();
    fetchReviewQueue();
    fetchSystemStatus();
    fetchSafetyStatus();
  }, []);

  const fetchEmails = async () => {
    try {
      const res = await axios.get(`${API}/emails`, { withCredentials: true });
      setEmails(res.data);
    } catch (error) {
      console.error("Failed to fetch emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewQueue = async () => {
    try {
      const res = await axios.get(`${API}/outreach/review-queue`, { withCredentials: true });
      setReviewQueue(res.data);
    } catch (error) {
      console.error("Failed to fetch review queue:", error);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const res = await axios.get(`${API}/email-system/status`, { withCredentials: true });
      setSystemStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch email system status:", error);
    }
  };

  const fetchSafetyStatus = async () => {
    try {
      const res = await axios.get(`${API}/outreach/safety/status`, { withCredentials: true });
      setSafetyStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch safety status:", error);
    }
  };

  const handleApproval = async (outreachId, action, notes = null) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/outreach/approve`, {
        outreach_id: outreachId,
        action: action,
        notes: notes
      }, { withCredentials: true });
      
      await fetchReviewQueue();
      setSelectedReviewItem(null);
    } catch (error) {
      console.error("Failed to perform action:", error);
      alert("Failed to perform action: " + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuppressContact = async (item) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/outreach/suppression/add`, null, {
        params: {
          email: item.contact_email,
          contact_id: item.contact_id,
          reason: "manual_contact_block",
          scope: "contact"
        },
        withCredentials: true
      });
      await handleApproval(item.outreach_id, "skip", "Contact suppressed");
    } catch (error) {
      console.error("Failed to suppress contact:", error);
      alert("Failed to suppress contact: " + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuppressCompany = async (item) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/outreach/suppression/add`, null, {
        params: {
          company_id: item.company_id,
          reason: "manual_company_block",
          scope: "company"
        },
        withCredentials: true
      });
      await handleApproval(item.outreach_id, "skip", "Company suppressed");
    } catch (error) {
      console.error("Failed to suppress company:", error);
      alert("Failed to suppress company: " + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const fetchRenderedPreview = async (outreachId) => {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/outreach/review-queue/rendered/${outreachId}`, { withCredentials: true });
      setRenderedPreview(res.data);
      setShowRenderedPreview(true);
    } catch (error) {
      console.error("Failed to fetch rendered preview:", error);
      alert("Failed to fetch rendered preview: " + (error.response?.data?.detail || error.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleEditDraft = async () => {
    if (!selectedReviewItem) return;
    setActionLoading(true);
    try {
      // Get the draft_id from the item or generate personalization first
      // For now, we'll update the outreach item directly
      await axios.patch(`${API}/outreach/pipeline/item/${selectedReviewItem.outreach_id}`, {
        subject: editingSubject,
        body_text: editingBody
      }, { withCredentials: true });
      
      setIsEditing(false);
      await fetchReviewQueue();
      setSelectedReviewItem(null);
    } catch (error) {
      console.error("Failed to save edit:", error);
      alert("Failed to save edit: " + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const filteredEmails = emails.filter(email => 
    email.to_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredReviewQueue = reviewQueue.filter(item =>
    item.company?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusStyle = (status) => {
    return STATUS_STYLES[status] || STATUS_STYLES.stored;
  };

  const getTierStyle = (tier) => {
    return TIER_STYLES[tier] || TIER_STYLES.D;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="email-outbox-page">
      <div className="page-header">
        <h1 className="page-title">Email Outbox</h1>
        <p className="page-subtitle">
          Review First workflow - all outreach requires human approval
        </p>
      </div>
      
      <div className="page-content">
        {/* Phase 3A Safety Status Banner */}
        {safetyStatus && (
          <div className={`rounded-lg p-4 mb-6 ${
            safetyStatus.can_send_production 
              ? "bg-green-50 border border-green-200" 
              : "bg-amber-50 border border-amber-200"
          }`}>
            <div className="flex items-start gap-3">
              {safetyStatus.can_send_production ? (
                <CheckCircle2 className="text-green-600 mt-0.5" size={20} />
              ) : (
                <AlertCircle className="text-amber-600 mt-0.5" size={20} />
              )}
              <div className="flex-1">
                <p className={`font-medium ${safetyStatus.can_send_production ? "text-green-900" : "text-amber-900"}`}>
                  {safetyStatus.can_send_production 
                    ? "Stock & Load Outreach: Production Ready" 
                    : "Stock & Load Outreach: Review Mode Only"}
                </p>
                <p className={`text-sm mt-1 ${safetyStatus.can_send_production ? "text-green-700" : "text-amber-700"}`}>
                  {!safetyStatus.safety_gates?.stock_load_outreach_enabled && 
                    "STOCK_LOAD_OUTREACH_ENABLED=false (brand-specific gate active). "}
                  {safetyStatus.safety_gates?.mock_mode_active && 
                    "Mock mode active - emails will be stored, not sent. "}
                  {!safetyStatus.is_configured && 
                    "Stock & Load sender not configured. "}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant={safetyStatus.safety_gates?.phase3a_enabled ? "default" : "destructive"} className="text-xs">
                    Phase 3A: {safetyStatus.safety_gates?.phase3a_enabled ? "ON" : "OFF"}
                  </Badge>
                  <Badge variant={safetyStatus.safety_gates?.stock_load_outreach_enabled ? "default" : "secondary"} className="text-xs">
                    S&L Gate: {safetyStatus.safety_gates?.stock_load_outreach_enabled ? "ON" : "OFF"}
                  </Badge>
                  <Badge variant={safetyStatus.safety_gates?.mock_mode_active ? "secondary" : "default"} className="text-xs">
                    Mock: {safetyStatus.safety_gates?.mock_mode_active ? "ON" : "OFF"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="review" className="flex items-center gap-2">
              <Clock size={16} />
              Review Queue
              {reviewQueue.length > 0 && (
                <Badge variant="secondary" className="ml-1">{reviewQueue.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <Send size={16} />
              Sent Emails
              {emails.length > 0 && (
                <Badge variant="secondary" className="ml-1">{emails.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative max-w-md my-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input 
              data-testid="search-input"
              placeholder="Search by company, contact, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Review Queue Tab */}
          <TabsContent value="review">
            <p className="text-sm text-muted-foreground mb-4">
              {filteredReviewQueue.length} item{filteredReviewQueue.length !== 1 ? 's' : ''} pending review
            </p>

            <Card>
              <CardContent className="p-0">
                {filteredReviewQueue.length === 0 ? (
                  <div className="empty-state">
                    <Clock className="empty-state-icon" />
                    <h3 className="empty-state-title">No items pending review</h3>
                    <p className="empty-state-text">
                      Draft emails will appear here when companies are qualified for outreach
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredReviewQueue.map((item) => {
                      const statusStyle = getStatusStyle(item.status);
                      const tierStyle = getTierStyle(item.contact_tier);
                      const company = item.company || {};
                      
                      return (
                        <div 
                          key={item.outreach_id}
                          className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => setSelectedReviewItem(item)}
                          data-testid={`review-item-${item.outreach_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Company & Contact */}
                              <div className="flex items-center gap-2 mb-1">
                                <Building2 size={14} className="text-muted-foreground" />
                                <span className="font-medium">{company.name || 'Unknown Company'}</span>
                                <Badge className={`${statusStyle.bg} ${statusStyle.text} text-xs`}>
                                  {statusStyle.label}
                                </Badge>
                                <Badge variant="outline" className="text-xs">S&L</Badge>
                              </div>
                              
                              {/* Contact info */}
                              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-1">
                                <span className="flex items-center gap-1">
                                  <User size={12} />
                                  {item.contact_name || 'Unknown'} 
                                  {item.contact_title && `(${item.contact_title})`}
                                </span>
                                {item.contact_tier && (
                                  <Badge className={`${tierStyle.bg} ${tierStyle.text} text-xs`}>
                                    {item.contact_tier}-Tier
                                  </Badge>
                                )}
                                {item.is_best_reachable_contact && (
                                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                    Best Reachable
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Subject preview */}
                              {item.subject && (
                                <p className="text-sm truncate">{item.subject}</p>
                              )}
                              
                              {/* Metadata row */}
                              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                                {company.fit_score !== undefined && (
                                  <span className="flex items-center gap-1">
                                    <Star size={10} />
                                    Fit: {company.fit_score}
                                  </span>
                                )}
                                {company.research_completeness !== undefined && (
                                  <span>Research: {company.research_completeness}%</span>
                                )}
                                {item.total_cost_usd > 0 && (
                                  <span className="flex items-center gap-1">
                                    <DollarSign size={10} />
                                    ${item.total_cost_usd.toFixed(2)}
                                  </span>
                                )}
                                {company.do_not_claim?.length > 0 && (
                                  <span className="flex items-center gap-1 text-amber-600">
                                    <AlertTriangle size={10} />
                                    {company.do_not_claim.length} warnings
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <Button variant="ghost" size="sm" className="ml-2">
                              <Eye size={16} className="mr-1" />
                              Review
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sent Emails Tab */}
          <TabsContent value="sent">
            <p className="text-sm text-muted-foreground mb-4">
              {filteredEmails.length} email{filteredEmails.length !== 1 ? 's' : ''} in outbox
            </p>

            <Card>
              <CardContent className="p-0">
                {filteredEmails.length === 0 ? (
                  <div className="empty-state">
                    <Mail className="empty-state-icon" />
                    <h3 className="empty-state-title">No emails yet</h3>
                    <p className="empty-state-text">
                      Emails will appear here after approval and sending
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredEmails.map((email) => {
                      const statusStyle = getStatusStyle(email.status);
                      return (
                        <div 
                          key={email.email_id}
                          className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => setSelectedEmail(email)}
                          data-testid={`email-row-${email.email_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium truncate">{email.to_email}</span>
                                <Badge className={`${statusStyle.bg} ${statusStyle.text} text-xs`}>
                                  {statusStyle.label}
                                </Badge>
                                {email.campaign_type === "stock_load" && (
                                  <Badge variant="outline" className="text-xs">S&L</Badge>
                                )}
                              </div>
                              <p className="text-sm text-foreground mb-1">{email.subject}</p>
                              <p className="text-xs text-muted-foreground">
                                From: {email.from_name} &lt;{email.from_email}&gt; • 
                                {new Date(email.sent_at).toLocaleString()}
                              </p>
                            </div>
                            <Button variant="ghost" size="sm" className="ml-2">
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Review Item Modal */}
        <Dialog open={!!selectedReviewItem} onOpenChange={() => {
          setSelectedReviewItem(null);
          setIsEditing(false);
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare size={20} />
                Review Outreach Draft
              </DialogTitle>
            </DialogHeader>
            
            {selectedReviewItem && (
              <div className="flex-1 overflow-auto space-y-4">
                {/* Company Section */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-medium flex items-center gap-2 mb-3">
                    <Building2 size={16} />
                    Company
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <span className="ml-2 font-medium">{selectedReviewItem.company?.name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Trade:</span>
                      <span className="ml-2">{selectedReviewItem.company?.trade || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Location:</span>
                      <span className="ml-2">{selectedReviewItem.company?.location || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Fit Score:</span>
                      <span className="ml-2 font-medium">{selectedReviewItem.company?.fit_score || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Research:</span>
                      <span className="ml-2">{selectedReviewItem.company?.research_completeness || 0}% complete</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Sales Angle:</span>
                      <span className="ml-2">{selectedReviewItem.company?.recommended_angle || 'General'}</span>
                    </div>
                  </div>
                  
                  {/* Do Not Claim Warnings */}
                  {selectedReviewItem.company?.do_not_claim?.length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-medium text-amber-900 flex items-center gap-2 text-sm">
                        <AlertTriangle size={14} />
                        Do Not Claim ({selectedReviewItem.company.do_not_claim.length})
                      </h4>
                      <ul className="mt-2 text-sm text-amber-800 space-y-1">
                        {selectedReviewItem.company.do_not_claim.slice(0, 5).map((warning, i) => (
                          <li key={i}>• {warning}</li>
                        ))}
                        {selectedReviewItem.company.do_not_claim.length > 5 && (
                          <li className="text-muted-foreground">
                            ... and {selectedReviewItem.company.do_not_claim.length - 5} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Contact Section */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-medium flex items-center gap-2 mb-3">
                    <User size={16} />
                    Contact
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Selected:</span>
                      <span className="ml-2 font-medium">{selectedReviewItem.contact_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Title:</span>
                      <span className="ml-2">{selectedReviewItem.contact_title || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <span className="ml-2">{selectedReviewItem.contact_email || 'Not available'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Tier:</span>
                      <Badge className={`ml-2 ${getTierStyle(selectedReviewItem.contact_tier).bg} ${getTierStyle(selectedReviewItem.contact_tier).text}`}>
                        {selectedReviewItem.contact_tier || 'Unknown'}-Tier
                      </Badge>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Score:</span>
                      <span className="ml-2">{selectedReviewItem.contact_score || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email Status:</span>
                      <span className="ml-2">{selectedReviewItem.email_verification_status || 'Unknown'}</span>
                    </div>
                  </div>
                  
                  {/* Best Contact Comparison */}
                  <div className="mt-3 flex gap-4">
                    {selectedReviewItem.is_best_contact && (
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        <Star size={12} className="mr-1" />
                        Best Contact
                      </Badge>
                    )}
                    {selectedReviewItem.is_best_reachable_contact && (
                      <Badge variant="outline" className="text-blue-600 border-blue-300">
                        <CheckCircle size={12} className="mr-1" />
                        Best Reachable Contact
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Message Section */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Mail size={16} />
                      Message
                    </h3>
                    <div className="flex gap-2">
                      {!isEditing && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => fetchRenderedPreview(selectedReviewItem.outreach_id)}
                            disabled={previewLoading}
                          >
                            <FileText size={14} className="mr-1" />
                            {previewLoading ? 'Loading...' : 'Preview Final Email'}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setEditingSubject(selectedReviewItem.subject || '');
                              setEditingBody(selectedReviewItem.body_text || '');
                              setIsEditing(true);
                            }}
                          >
                            <FileEdit size={14} className="mr-1" />
                            Edit
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Subject</label>
                        <Input 
                          value={editingSubject}
                          onChange={(e) => setEditingSubject(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Body (editable portion only - compliance footer is added automatically)</label>
                        <Textarea 
                          value={editingBody}
                          onChange={(e) => setEditingBody(e.target.value)}
                          className="mt-1 min-h-[200px]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleEditDraft} disabled={actionLoading}>
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={() => setIsEditing(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <span className="text-sm text-muted-foreground">Subject:</span>
                        <p className="font-medium">{selectedReviewItem.subject || 'No subject'}</p>
                      </div>
                      <div className="border border-border rounded-lg p-4 bg-white">
                        {selectedReviewItem.body_html ? (
                          <div dangerouslySetInnerHTML={{ __html: selectedReviewItem.body_html }} />
                        ) : (
                          <p className="whitespace-pre-wrap">{selectedReviewItem.body_text || 'No content'}</p>
                        )}
                      </div>
                      
                      {/* Personalization Evidence */}
                      {selectedReviewItem.personalization_evidence?.length > 0 && (
                        <div className="mt-3 text-sm">
                          <span className="text-muted-foreground">Evidence Used:</span>
                          <ul className="mt-1 space-y-1">
                            {selectedReviewItem.personalization_evidence.map((ev, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle2 size={12} className="text-green-500 mt-0.5" />
                                {ev}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Compliance Footer Note */}
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> A compliance footer with postal address and unsubscribe link will be automatically appended at send time. Click &quot;Preview Final Email&quot; to see the exact email that will be sent.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Cost Section */}
                {(selectedReviewItem.enrichment_cost_usd > 0 || selectedReviewItem.personalization_cost_usd > 0) && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h3 className="font-medium flex items-center gap-2 mb-3">
                      <DollarSign size={16} />
                      Acquisition Cost
                    </h3>
                    <div className="flex gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Enrichment:</span>
                        <span className="ml-2">${(selectedReviewItem.enrichment_cost_usd || 0).toFixed(4)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Personalization:</span>
                        <span className="ml-2">${(selectedReviewItem.personalization_cost_usd || 0).toFixed(4)}</span>
                      </div>
                      <div className="font-medium">
                        <span className="text-muted-foreground">Total:</span>
                        <span className="ml-2">${(selectedReviewItem.total_cost_usd || 0).toFixed(4)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <div className="flex w-full justify-between">
                {/* Destructive Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleSuppressContact(selectedReviewItem)}
                    disabled={actionLoading}
                    className="text-red-600 hover:text-red-700"
                  >
                    <UserX size={14} className="mr-1" />
                    Suppress Contact
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleSuppressCompany(selectedReviewItem)}
                    disabled={actionLoading}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Building size={14} className="mr-1" />
                    Suppress Company
                  </Button>
                </div>
                
                {/* Primary Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => handleApproval(selectedReviewItem?.outreach_id, 'regenerate')}
                    disabled={actionLoading}
                  >
                    <RefreshCw size={14} className="mr-1" />
                    Regenerate
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleApproval(selectedReviewItem?.outreach_id, 'skip')}
                    disabled={actionLoading}
                  >
                    <SkipForward size={14} className="mr-1" />
                    Skip
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => handleApproval(selectedReviewItem?.outreach_id, 'reject')}
                    disabled={actionLoading}
                  >
                    <ThumbsDown size={14} className="mr-1" />
                    Reject
                  </Button>
                  <Button 
                    onClick={() => handleApproval(selectedReviewItem?.outreach_id, 'approve')}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <ThumbsUp size={14} className="mr-1" />
                    Approve
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Preview Modal */}
        <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Email Preview</DialogTitle>
            </DialogHeader>
            {selectedEmail && (
              <div className="flex-1 overflow-auto">
                <div className="bg-muted rounded-lg p-4 mb-4 space-y-2">
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-16">To:</span>
                    <span className="text-sm">{selectedEmail.to_email}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-16">From:</span>
                    <span className="text-sm">{selectedEmail.from_name} &lt;{selectedEmail.from_email}&gt;</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-16">Subject:</span>
                    <span className="text-sm font-medium">{selectedEmail.subject}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-16">Date:</span>
                    <span className="text-sm">{new Date(selectedEmail.sent_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="border border-border rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={selectedEmail.html_body}
                    title="Email Content"
                    className="w-full h-[400px] border-none bg-white"
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Rendered Email Preview Modal */}
        <Dialog open={showRenderedPreview} onOpenChange={() => {
          setShowRenderedPreview(false);
          setRenderedPreview(null);
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText size={20} />
                Final Rendered Email Preview
              </DialogTitle>
            </DialogHeader>
            {renderedPreview && (
              <div className="flex-1 overflow-auto space-y-4">
                {/* Compliance Status */}
                {renderedPreview.compliance_ready ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-green-800 font-medium">Compliance Ready</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">
                      This email includes the required postal address and unsubscribe link.
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="text-red-600" size={16} />
                      <span className="text-red-800 font-medium">Compliance Missing</span>
                    </div>
                    <p className="text-sm text-red-700 mt-1">
                      {renderedPreview.compliance_missing || 'Brand not configured for compliant sending'}
                    </p>
                  </div>
                )}

                {/* Email Headers */}
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-20">From:</span>
                    <span className="text-sm">{renderedPreview.from_name} &lt;{renderedPreview.from_email}&gt;</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-20">Reply-To:</span>
                    <span className="text-sm">{renderedPreview.reply_to}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-20">To:</span>
                    <span className="text-sm">{renderedPreview.to_email}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-sm text-muted-foreground w-20">Subject:</span>
                    <span className="text-sm font-medium">{renderedPreview.subject}</span>
                  </div>
                </div>

                {/* Compliance Footer Details */}
                {renderedPreview.compliance_footer && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 flex items-center gap-2 mb-3">
                      <MapPin size={14} />
                      Compliance Footer (Automatically Appended)
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Brand:</span>
                        <span className="ml-2 text-blue-900">{renderedPreview.compliance_footer.brand_name}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Postal Address:</span>
                        <span className="ml-2 text-blue-900">{renderedPreview.compliance_footer.postal_address}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-blue-700">Unsubscribe URL:</span>
                      <a 
                        href={renderedPreview.compliance_footer.unsubscribe_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 underline flex items-center gap-1 inline-flex"
                      >
                        <Link size={12} />
                        {renderedPreview.compliance_footer.unsubscribe_url?.substring(0, 60)}...
                      </a>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      This footer cannot be edited. It is required for CAN-SPAM compliance.
                    </p>
                  </div>
                )}

                {/* Final Rendered Email */}
                <div>
                  <h4 className="font-medium mb-2">Final Email (Exactly as it will be sent)</h4>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={renderedPreview.final_body_html}
                      title="Final Email Content"
                      className="w-full h-[400px] border-none bg-white"
                    />
                  </div>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <h4 className="font-medium mb-2">Personalization Evidence</h4>
                    {renderedPreview.personalization_evidence?.length > 0 ? (
                      <ul className="space-y-1">
                        {renderedPreview.personalization_evidence.map((ev, i) => (
                          <li key={i} className="flex items-start gap-2 text-muted-foreground">
                            <CheckCircle2 size={12} className="text-green-500 mt-0.5" />
                            {ev}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground">No evidence recorded</p>
                    )}
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <h4 className="font-medium mb-2 text-amber-700">Do Not Claim</h4>
                    {renderedPreview.do_not_claim?.length > 0 ? (
                      <ul className="space-y-1">
                        {renderedPreview.do_not_claim.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-amber-700">
                            <AlertTriangle size={12} className="mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground">None</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button variant="outline" onClick={() => {
                setShowRenderedPreview(false);
                setRenderedPreview(null);
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
